'use client'

import { Fragment, useEffect, useMemo, useState } from 'react'
import { CartSummary } from './cart-summary'
import {
  useLayout,
  useResponsive,
  useSiteContext,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import classnames from 'classnames'
import { NotificationBanner } from '@dustin-web/microsite-components/src/banners'
import { useI18n } from '@dustin-web/microsite-components/src/i18n'
import useShoppingCart from '@dustin-web/microsite-data/src/hooks/use-shopping-cart-internal'
import { isNotNullOrUndefined } from '@dustin-web/microsite-data/src/utils'
import { ProductWithCategoryType } from '@dustin-web/product-components/src/utils/digital-data-types'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import Button from '@dustin-web/microsite-components/src/button'
import Drawer from '@dustin-web/microsite-components/src/drawer'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import { CartDrawer } from './cart-drawer'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const Sidebar = () => {
  const { header } = useLayout()
  const { isAuthenticated } = useAuth()
  const { translate, translationsExist } = useI18n()
  const { breakpoints } = useResponsive()
  const { currencyCode } = useSiteContext()
  const { showPricesInclVat } = useUserSettings()
  const { isUpdating, cartItems, totalPriceExcludingVat } = useShoppingCart({
    withRequiredEndUserInformation: true,
  })
  const [openDrawer, setOpenDrawer] = useState(false)
  const [disableCheckoutButton, setDisableCheckoutButton] = useState(false)
  const [showEndUserInformationValidation, setShowEndUserInformationValidation] = useState(false)
  const { enabled: isShowExVatTextFor0VatEnabled } = useFeatureFlag('show-ex-vat-text-for-0-vat')

  // TODO: Make hook/util?
  const reachedMaxAvailableQuantity = useMemo(() => {
    const reachedMaxQuantity = cartItems.filter(
      item =>
        item.product?.availability?.maxAvailableQuantity != null &&
        item.product?.availability?.maxAvailableQuantity < item.quantity
    )
    return reachedMaxQuantity.length > 0
  }, [cartItems])

  useEffect(() => {
    if (isUpdating || reachedMaxAvailableQuantity || showEndUserInformationValidation) {
      setDisableCheckoutButton(true)
    } else {
      setDisableCheckoutButton(false)
    }
  }, [isUpdating, reachedMaxAvailableQuantity, showEndUserInformationValidation])

  const requiredEndUserInformationMissing = cartItems
    .map(cartItem => {
      const haveMandatoryEndUserInformationMissing =
        cartItem.product?.requiredEndUserInformation?.some(
          requiredEndUserInfo =>
            requiredEndUserInfo.isMandatory &&
            !cartItem.endUserInformation?.find(
              cartItemEndUserInformation =>
                cartItemEndUserInformation?.lineNumber === requiredEndUserInfo.lineNumber
            )?.value
        )

      if (!haveMandatoryEndUserInformationMissing) {
        return null
      }

      return cartItem.product
    })
    .filter(isNotNullOrUndefined)

  const showReverseVatInfo =
    translationsExist('Reverse_Vat_info') &&
    isShowExVatTextFor0VatEnabled &&
    showPricesInclVat &&
    cartItems.some(x => x.product?.price?.priceVat === 0)

  const checkoutInfoBanners: React.ReactNode[] = []

  if (
    showEndUserInformationValidation &&
    !reachedMaxAvailableQuantity &&
    requiredEndUserInformationMissing?.length > 0
  ) {
    checkoutInfoBanners.push(
      <NotificationBanner
        type="ERROR"
        closeable={true}
        description={translate('Checkout_ItemsInCartMissingRequiredInformation')}
        list={requiredEndUserInformationMissing.map(missingInfo => missingInfo.displayName || '')}
      />
    )
  }

  if (reachedMaxAvailableQuantity) {
    checkoutInfoBanners.push(
      <NotificationBanner type="ERROR" description={translate('Cart_MaxQuantity_GeneralError')} />
    )
  }

  useEffect(() => {
    if (!requiredEndUserInformationMissing.length) {
      setShowEndUserInformationValidation(false)
    }
  }, [requiredEndUserInformationMissing])

  const shouldShowEndUserInformationValidation = () => {
    if (requiredEndUserInformationMissing.length && !showEndUserInformationValidation) {
      return true
    }
    return false
  }

  const handleGoToCheckout = (event: React.MouseEvent) => {
    if (shouldShowEndUserInformationValidation()) {
      setShowEndUserInformationValidation(true)
      event.preventDefault()
    } else {
      try {
        ProductDigitalData.pushBeginCheckout(
          cartItems
            .map((x, i) => {
              return x.product
                ? ({
                  ...x.product,
                  quantity: x.quantity,
                  productListIndex: i,
                } as ProductWithCategoryType)
                : null
            })
            .filter(isNotNullOrUndefined),
          '/shoppingcart',
          currencyCode,
          totalPriceExcludingVat
        )
      } catch { } // eslint-disable-line
    }
  }

  return (
    <div className="c-shoppingcart__sidebar-container u-mb-16 u-mb-xl-32 col-12 col-xl-3 align-self-stretch">
      <div
        style={{
          marginTop: breakpoints.min.XL ? 32 : 0,
          top: header.height.css,
        }}
        className="c-shoppingcart__sidebar">
        <div className="u-px-16 u-bg-white u-radius-4 u-mb-16">
          <CartSummary />
          {showReverseVatInfo && (
            // || spoofedUi?.reverseVatInfo
            <div className="c-box c-box--border t-light-blue u-mb-16">
              <div className="u-fw-bold">
                {translate('Reverse_Vat_info')}
                {/* // || spoofedUi?.reverseVatInfo */}
              </div>
            </div>
          )}

          {!isAuthenticated ? (
            <>
              <Drawer
                className={classnames('u-px-0 u-py-0')}
                customBreakpointWidth={{ xl: '50vw' }}
                maxWidth="60rem"
                visible={openDrawer}
                usePortal={true}
                onClose={() => setOpenDrawer(false)}>
                <CartDrawer cartItems={cartItems} />
              </Drawer>
              <Button
                className={classnames('c-btn c-btn--block c-btn--checkout u-mb-16', {
                  'u-disabled': disableCheckoutButton,
                })}
                text={translate('Cart_GoToCheckout')}
                onClick={(event: React.MouseEvent) => {
                  if (shouldShowEndUserInformationValidation()) {
                    setShowEndUserInformationValidation(true)
                    event.preventDefault()
                  } else {
                    setOpenDrawer(true),
                      DigitalData.pushEvent({ event: 'show_gotocheckout_drawer' })
                  }
                }}
              />
            </>
          ) : (
            <a
              data-test-id="go-to-checkout-button"
              role="button"
              className={classnames('c-btn c-btn--block c-btn--checkout u-mb-16', {
                'u-disabled': disableCheckoutButton,
              })}
              href="/checkoutflow"
              onClick={e => handleGoToCheckout(e)}>
              {translate('Cart_GoToCheckout')}
            </a>
          )}
        </div>

        {checkoutInfoBanners.map((banner, i) => (
          <Fragment key={i}>{banner}</Fragment>
        ))}
      </div>
    </div>
  )
}

export default Sidebar
